import type { ChangeEvent, ClipboardEvent, CSSProperties, FormEvent, ReactNode } from 'react'

export interface AssistantInputProps {
  input: string
  onInputChange: (value: string) => void
  onSubmit: (e: FormEvent) => void
  placeholder: string
  /** True while a turn is in flight (sending/streaming) OR rate-limited. */
  disabled: boolean
  streaming: boolean
  isRateLimited: boolean
  onStop: () => void
  /** Anonymous (public-dashboard) mode — shows the "conversations are stored" notice. */
  anon: boolean
  /** Captured still attached to the next turn, or null. */
  activeStill: string | null
  onClearStill: () => void
  controlStyle?: CSSProperties
  mutedBorder?: string
  /** Send button color overrides (dashboard appearance). */
  sendButtonStyle?: CSSProperties
  /**
   * Speech-to-text for the composer (Mode 2). OPTIONAL: a caller that omits it
   * renders exactly the composer this component rendered before, which is why
   * the sitewide assistant is unaffected until it opts in.
   *
   * Shaped as `useSpeechInput`'s result rather than a pile of flat props, so the
   * hook stays the single owner of recogniser state and this component keeps
   * being pure and props-driven.
   */
  speech?: {
    supported: boolean
    listening: boolean
    transcript: string
    error: string | null
    start: () => void
    stop: () => void
  }
  /**
   * Attachments (sitewide assistant). All optional, so the dashboard chatbot's composer is unchanged:
   * the chips above the input, the paperclip beside it, a paste handler for pictures, and why the
   * message cannot be sent yet — shown under the input and holding the Send button.
   */
  attachmentsSlot?: ReactNode
  attachAction?: ReactNode
  onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void
  notice?: string | null
  submitBlockedReason?: string | null
}

/**
 * Composer: anon notice, active-still chip, text input, Stop/Send. Pure/props-driven
 * — no context reads, no machine access. The caller owns all state + side effects.
 */
export function AssistantInput({
  input,
  onInputChange,
  onSubmit,
  placeholder,
  disabled,
  streaming,
  isRateLimited,
  onStop,
  anon,
  activeStill,
  onClearStill,
  controlStyle,
  mutedBorder,
  sendButtonStyle,
  speech,
  attachmentsSlot,
  attachAction,
  onPaste,
  notice,
  submitBlockedReason,
}: AssistantInputProps) {
  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-[0.25em] border-t border-gray-200 p-[0.5em] dark:border-gray-700"
      style={{ borderColor: mutedBorder }}
    >
      {anon && (
        <p className="text-[0.62em] text-gray-400 dark:text-gray-500">
          Conversations on this public dashboard are stored.
        </p>
      )}
      {activeStill && (
        <div className="flex items-center gap-[0.5em] text-[0.62em] text-gray-500 dark:text-gray-400">
          <img src={activeStill} alt="" className="h-6 w-6 rounded object-cover" />
          <span>Still attached — follow-ups can reference it</span>
          <button
            type="button"
            onClick={onClearStill}
            className="rounded px-[0.25em] hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Detach still"
          >
            ✕
          </button>
        </div>
      )}
      {/* What was heard, before it becomes a turn. Without it an operator
          cannot tell a mis-heard word from a dead microphone. */}
      {speech?.listening && (
        <div className="mb-[0.35em] flex items-center gap-[0.4em] text-[0.75em] text-gray-600 dark:text-gray-300">
          <span className="inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-red-500" aria-hidden="true" />
          <span aria-live="polite">{speech.transcript || 'Listening…'}</span>
        </div>
      )}
      {speech?.error === 'not-allowed' && (
        <p className="mb-[0.35em] text-[0.75em] text-amber-700 dark:text-amber-400">
          The microphone is blocked for this page.
        </p>
      )}
      {attachmentsSlot}
      {(submitBlockedReason || notice) && (
        <p role="status" className="text-[0.68em] text-amber-700 dark:text-amber-400">
          {submitBlockedReason ?? notice}
        </p>
      )}
      <div className="flex gap-[0.5em]">
        {attachAction}
        <input
          value={input}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value)}
          onPaste={onPaste}
          placeholder={placeholder}
          disabled={disabled}
          className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-[0.5em] py-[0.25em] text-gray-800 focus:border-primary-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          style={{ ...controlStyle, fontSize: '0.875em' }}
        />
        {/* Hidden, not disabled, where the browser has no recogniser: a
            permanently-greyed mic button is a promise the page cannot keep. */}
        {speech?.supported && (
          <button
            type="button"
            onClick={() => (speech.listening ? speech.stop() : speech.start())}
            disabled={disabled}
            aria-pressed={speech.listening}
            aria-label={speech.listening ? 'Stop listening' : 'Speak to the assistant'}
            title={speech.listening ? 'Stop listening' : 'Speak to the assistant'}
            className={`shrink-0 rounded-md px-[0.6em] py-[0.25em] font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
              speech.listening
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
            }`}
            style={{ fontSize: '0.875em' }}
          >
            {speech.listening ? '■' : '🎤'}
          </button>
        )}
        {streaming && !isRateLimited ? (
          <button
            type="button"
            onClick={onStop}
            className="shrink-0 rounded-md bg-red-600 px-[0.75em] py-[0.25em] font-medium text-white hover:bg-red-700"
            style={{ fontSize: '0.875em' }}
          >
            Stop
          </button>
        ) : (
          <button
            type="submit"
            disabled={disabled || !input.trim() || Boolean(submitBlockedReason)}
            title={isRateLimited ? 'Daily budget reached — try again tomorrow.' : undefined}
            className="shrink-0 rounded-md bg-primary-600 px-[0.75em] py-[0.25em] font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ ...sendButtonStyle, fontSize: '0.875em' }}
          >
            Send
          </button>
        )}
      </div>
    </form>
  )
}
